package com.karyadina.service

import com.karyadina.model.*
import kotlinx.coroutines.*
import com.karyadina.util.currentTimeMillis

interface QuickNotesService {
    // Note operations
    fun createNote(note: QuickNote): QuickNote
    fun updateNote(note: QuickNote): QuickNote?
    fun deleteNote(noteId: String): Boolean
    fun restoreNote(note: QuickNote): QuickNote
    fun getNote(noteId: String): QuickNote?
    fun getAllNotes(): List<QuickNote>
    
    // Filtering
    fun getNotesByType(type: NoteType): List<QuickNote>
    fun getNotesByFolder(folderId: String): List<QuickNote>
    fun getPinnedNotes(): List<QuickNote>
    fun getArchivedNotes(): List<QuickNote>
    fun getNotesByTag(tag: String): List<QuickNote>
    
    // Search
    fun searchNotes(query: String): List<QuickNote>
    
    // Quick actions
    fun togglePin(noteId: String): Boolean
    fun toggleArchive(noteId: String): Boolean
    fun addTag(noteId: String, tag: String): Boolean
    fun removeTag(noteId: String, tag: String): Boolean
    
    // Checklist operations
    fun updateChecklistItem(noteId: String, itemId: String, checked: Boolean): Boolean
    fun addChecklistItem(noteId: String, item: ChecklistItem): Boolean
    fun deleteChecklistItem(noteId: String, itemId: String): Boolean
    
    // Folder operations
    fun createFolder(folder: NoteFolder): NoteFolder
    fun getAllFolders(): List<NoteFolder>
    fun deleteFolder(folderId: String): Boolean
    
    // Statistics
    fun getNotesStats(): NotesStats
    /** Re-read all data from persistent storage (e.g. after cloud sync import) */
    fun refreshFromStorage()
}

data class NotesStats(
    val totalNotes: Int,
    val todayNotes: Int,
    val weekNotes: Int,
    val pinnedCount: Int,
    val archivedCount: Int,
    val checklistsCount: Int,
    val completedTasks: Int,
    val totalTasks: Int
)

class QuickNotesServiceImpl(
    private val storage: QuickNotesStorage = QuickNotesStorage()
) : QuickNotesService {
    private val notes = mutableListOf<QuickNote>()
    private val folders = mutableListOf<NoteFolder>()
    
    init {
        // Load saved notes from storage
        notes.addAll(storage.loadNotes())
        
        // Listen for storage refresh signals
        kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.Dispatchers.Default).launch {
            AppStorage.refreshNotifier.collect {
                refreshFromStorage()
            }
        }
    }

    override fun refreshFromStorage() {
        notes.clear()
        notes.addAll(storage.loadNotes())
    }
    
    override fun createNote(note: QuickNote): QuickNote {
        val newNote = note.copy(
            id = generateId(),
            createdAt = currentTimeMillis(),
            updatedAt = currentTimeMillis()
        )
        notes.add(0, newNote) // Add to top
        storage.saveNotes(notes)
        return newNote
    }
    
    override fun updateNote(note: QuickNote): QuickNote? {
        val index = notes.indexOfFirst { it.id == note.id }
        if (index == -1) return null
        
        val updated = note.copy(updatedAt = currentTimeMillis())
        notes[index] = updated
        storage.saveNotes(notes)
        return updated
    }
    
    override fun deleteNote(noteId: String): Boolean {
        val index = notes.indexOfFirst { it.id == noteId }
        return if (index >= 0) {
            notes.removeAt(index)
            storage.saveNotes(notes)
            true
        } else false
    }
    
    override fun restoreNote(note: QuickNote): QuickNote {
        // Add back to the list at the beginning or sort order
        notes.add(0, note)
        storage.saveNotes(notes)
        return note
    }
    
    override fun getNote(noteId: String): QuickNote? {
        return notes.firstOrNull { it.id == noteId }
    }
    
    override fun getAllNotes(): List<QuickNote> {
        return notes.filter { !it.isArchived }
            .sortedByDescending { if (it.isPinned) Long.MAX_VALUE else it.updatedAt }
    }
    
    override fun getNotesByType(type: NoteType): List<QuickNote> {
        return notes.filter { it.noteType == type.name && !it.isArchived }
            .sortedByDescending { if (it.isPinned) Long.MAX_VALUE else it.updatedAt }
    }
    
    override fun getNotesByFolder(folderId: String): List<QuickNote> {
        return notes.filter { it.subject == folderId && !it.isArchived }
            .sortedByDescending { if (it.isPinned) Long.MAX_VALUE else it.updatedAt }
    }
    
    override fun getPinnedNotes(): List<QuickNote> {
        return notes.filter { it.isPinned && !it.isArchived }
            .sortedByDescending { it.updatedAt }
    }
    
    override fun getArchivedNotes(): List<QuickNote> {
        return notes.filter { it.isArchived }
            .sortedByDescending { it.updatedAt }
    }
    
    override fun getNotesByTag(tag: String): List<QuickNote> {
        return notes.filter { tag in it.tags && !it.isArchived }
            .sortedByDescending { if (it.isPinned) Long.MAX_VALUE else it.updatedAt }
    }
    
    override fun searchNotes(query: String): List<QuickNote> {
        val lowerQuery = query.lowercase()
        return notes.filter { note ->
            !note.isArchived && (
                note.title.lowercase().contains(lowerQuery) ||
                note.content.lowercase().contains(lowerQuery) ||
                note.tags.any { it.lowercase().contains(lowerQuery) }
            )
        }.sortedByDescending { if (it.isPinned) Long.MAX_VALUE else it.updatedAt }
    }
    
    override fun togglePin(noteId: String): Boolean {
        val note = notes.firstOrNull { it.id == noteId } ?: return false
        val updated = note.copy(isPinned = !note.isPinned, updatedAt = currentTimeMillis())
        notes[notes.indexOfFirst { it.id == noteId }] = updated
        storage.saveNotes(notes)
        return updated.isPinned
    }
    
    override fun toggleArchive(noteId: String): Boolean {
        val note = notes.firstOrNull { it.id == noteId } ?: return false
        val updated = note.copy(isArchived = !note.isArchived, updatedAt = currentTimeMillis())
        notes[notes.indexOfFirst { it.id == noteId }] = updated
        storage.saveNotes(notes)
        return updated.isArchived
    }
    
    override fun addTag(noteId: String, tag: String): Boolean {
        val note = notes.firstOrNull { it.id == noteId } ?: return false
        if (tag in note.tags) return false
        
        val updated = note.copy(
            tags = note.tags + tag,
            updatedAt = currentTimeMillis()
        )
        notes[notes.indexOfFirst { it.id == noteId }] = updated
        storage.saveNotes(notes)
        return true
    }
    
    override fun removeTag(noteId: String, tag: String): Boolean {
        val note = notes.firstOrNull { it.id == noteId } ?: return false
        if (tag !in note.tags) return false
        
        val updated = note.copy(
            tags = note.tags - tag,
            updatedAt = currentTimeMillis()
        )
        notes[notes.indexOfFirst { it.id == noteId }] = updated
        storage.saveNotes(notes)
        return true
    }
    
    override fun updateChecklistItem(noteId: String, itemId: String, checked: Boolean): Boolean {
        val note = notes.firstOrNull { it.id == noteId } ?: return false
        if (note.noteType != NoteType.CHECKLIST.name) return false
        
        val updatedItems = note.checklistItems.map { item ->
            if (item.id == itemId) item.copy(checked = checked) else item
        }
        
        val updated = note.copy(
            checklistItems = updatedItems,
            updatedAt = currentTimeMillis()
        )
        notes[notes.indexOfFirst { it.id == noteId }] = updated
        storage.saveNotes(notes)
        return true
    }
    
    override fun addChecklistItem(noteId: String, item: ChecklistItem): Boolean {
        val note = notes.firstOrNull { it.id == noteId } ?: return false
        if (note.noteType != NoteType.CHECKLIST.name) return false
        
        val newItem = item.copy(id = generateId())
        val updated = note.copy(
            checklistItems = note.checklistItems + newItem,
            updatedAt = currentTimeMillis()
        )
        notes[notes.indexOfFirst { it.id == noteId }] = updated
        storage.saveNotes(notes)
        return true
    }
    
    override fun deleteChecklistItem(noteId: String, itemId: String): Boolean {
        val note = notes.firstOrNull { it.id == noteId } ?: return false
        if (note.noteType != NoteType.CHECKLIST.name) return false
        
        val updated = note.copy(
            checklistItems = note.checklistItems.filter { it.id != itemId },
            updatedAt = currentTimeMillis()
        )
        notes[notes.indexOfFirst { it.id == noteId }] = updated
        storage.saveNotes(notes)
        return true
    }
    
    override fun createFolder(folder: NoteFolder): NoteFolder {
        val newFolder = folder.copy(
            id = generateId(),
            createdAt = currentTimeMillis()
        )
        folders.add(newFolder)
        return newFolder
    }
    
    override fun getAllFolders(): List<NoteFolder> {
        return folders.sortedBy { it.name }
    }
    
    override fun deleteFolder(folderId: String): Boolean {
        // Move notes from folder to root (using subject field as folder)
        notes.filter { it.subject == folderId }.forEach { note ->
            val updated = note.copy(subject = null)
            notes[notes.indexOfFirst { it.id == note.id }] = updated
        }
        val index = folders.indexOfFirst { it.id == folderId }
        return if (index >= 0) {
            folders.removeAt(index)
            true
        } else false
    }
    
    override fun getNotesStats(): NotesStats {
        val now = currentTimeMillis()
        val oneDayAgo = now - 24 * 60 * 60 * 1000
        val oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000
        
        val todayNotes = notes.count { it.createdAt >= oneDayAgo }
        val weekNotes = notes.count { it.createdAt >= oneWeekAgo }
        
        val checklistNotes = notes.filter { it.noteType == NoteType.CHECKLIST.name }
        val totalTasks = checklistNotes.sumOf { it.checklistItems.size }
        val completedTasks = checklistNotes.sumOf { note ->
            note.checklistItems.count { it.checked }
        }
        
        return NotesStats(
            totalNotes = notes.count { !it.isArchived },
            todayNotes = todayNotes,
            weekNotes = weekNotes,
            pinnedCount = notes.count { it.isPinned },
            archivedCount = notes.count { it.isArchived },
            checklistsCount = checklistNotes.size,
            completedTasks = completedTasks,
            totalTasks = totalTasks
        )
    }
    
    private fun generateId(): String {
        return "NOTE_${currentTimeMillis()}_${(1000..9999).random()}"
    }
}
